import { useCredits } from "../contexts/CreditContext";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Compact credit badge for the navbar.
 * Shows "742 / 1000" with breakdown tooltip when purchased credits exist.
 */
export default function CreditBadge() {
  const ctx = useCredits();
  const navigate = useNavigate();
  if (!ctx?.credits) return null;

  const { remaining, monthlyRemaining, purchasedRemaining, total, pct, isLow, isExhausted } = ctx;

  const color = isExhausted
    ? "text-destructive"
    : isLow
    ? "text-amber-400"
    : "text-primary";

  const ringColor = isExhausted
    ? "stroke-destructive"
    : isLow
    ? "stroke-amber-400"
    : "stroke-primary";

  const title = purchasedRemaining > 0
    ? `Monthly: ${monthlyRemaining} + Purchased: ${purchasedRemaining}`
    : `Monthly: ${monthlyRemaining}`;

  return (
    <button
      type="button"
      className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-card border border-border text-sm select-none transition-colors hover:border-primary/40 hover:bg-accent/40"
      data-testid="credit-badge"
      title={title}
      onClick={() => navigate("/buy-credits")}
    >
      {/* mini donut */}
      <svg width="20" height="20" viewBox="0 0 20 20" className="shrink-0">
        <circle
          cx="10"
          cy="10"
          r="8"
          fill="none"
          strokeWidth="3"
          className="stroke-muted/30"
        />
        <circle
          cx="10"
          cy="10"
          r="8"
          fill="none"
          strokeWidth="3"
          className={ringColor}
          strokeDasharray={`${pct * 0.5} 50`}
          strokeLinecap="round"
          transform="rotate(-90 10 10)"
        />
      </svg>

      <Sparkles className={`w-3.5 h-3.5 ${color}`} />
      <span className={`font-medium tabular-nums ${color}`} data-testid="credit-badge-remaining">
        {remaining.toLocaleString()}
      </span>
      <span className="text-muted-foreground">/</span>
      <span className="text-muted-foreground tabular-nums">
        {total.toLocaleString()}
      </span>
      {purchasedRemaining > 0 && (
        <span className="text-xs text-emerald-400 font-medium" data-testid="credit-badge-purchased">
          +{purchasedRemaining.toLocaleString()}
        </span>
      )}
    </button>
  );
}
